/* Montague — theme scripts */
(() => {
  const body = document.body;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /* ───────── Announcement bar: one message at a time ───────── */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      const items = $$('.ann__item', this);
      if (items.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let i = 0;
      const speed = parseFloat(getComputedStyle(this).getPropertyValue('--speed')) || 5;
      this.timer = setInterval(() => {
        items[i].classList.remove('is-on');
        i = (i + 1) % items.length;
        items[i].classList.add('is-on');
      }, speed * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ───────── Header: menu panels, mobile menu, search ───────── */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.panels = $$('[data-mega-panel]', this);
      this.buttons = $$('[data-mega]', this);
      this.buttons.forEach(b => {
        b.addEventListener('click', () => this.openMega(b.getAttribute('aria-expanded') === 'true' ? null : b.dataset.mega));
        b.addEventListener('mouseenter', () => { if (matchMedia('(hover: hover)').matches) this.openMega(b.dataset.mega); });
      });
      this.addEventListener('mouseleave', () => { if (matchMedia('(hover: hover)').matches) this.openMega(null); });
      $$('.hdr__nav > a, .hdr__logo, .hdr__util', this).forEach(el => el.addEventListener('mouseenter', () => this.openMega(null)));

      // Mobile menu
      this.mobile = $('[data-mobile-menu]');
      this.toggle = $('[data-menu-toggle]', this);
      this.toggle.addEventListener('click', () => this.setMenu(!body.classList.contains('menu-open')));
      $$('[data-menu-close]').forEach(el => el.addEventListener('click', () => this.setMenu(false)));

      // Search
      this.search = $('[data-search-panel]', this);
      this.input = $('[data-search-input]', this);
      this.results = $('[data-search-results]', this);
      $('[data-search-open]', this).addEventListener('click', e => { e.preventDefault(); this.setSearch(this.search.hidden); });
      $('[data-search-close]', this).addEventListener('click', () => this.setSearch(false));
      let t;
      this.input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.suggest(), 180); });

      // Shadow line once the page scrolls
      const onScroll = () => this.classList.toggle('is-scrolled', scrollY > 4);
      addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
    openMega(id) {
      this.buttons.forEach(b => b.setAttribute('aria-expanded', b.dataset.mega === id));
      this.panels.forEach(p => { p.hidden = p.dataset.megaPanel !== id; });
      body.classList.toggle('mega-open', !!id);
      if (id) this.setSearch(false);
    }
    setMenu(open) {
      // The announcement bar scrolls away, so place the menu under wherever the header is now.
      if (open && this.mobile) this.mobile.style.top = Math.max(0, this.getBoundingClientRect().bottom) + 'px';
      body.classList.toggle('menu-open', open);
      this.toggle.setAttribute('aria-expanded', open);
      this.toggle.textContent = open ? this.toggle.dataset.close || 'Close' : this.toggle.dataset.open || 'Menu';
      this.mobile && this.mobile.setAttribute('aria-hidden', !open);
    }
    setSearch(open) {
      this.search.hidden = !open;
      body.classList.toggle('search-open', open);
      if (open) { this.openMega(null); this.setMenu(false); setTimeout(() => this.input.focus(), 30); }
    }
    async suggest() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; return; }
      try {
        const res = await fetch(`${theme.routes.search}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-predictive]', doc);
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (e) { /* keep the last results */ }
    }
  }
  customElements.define('site-header', SiteHeader);
  const header = () => $('site-header');
  const menuToggle = $('[data-menu-toggle]');
  if (menuToggle) { menuToggle.dataset.open = menuToggle.textContent.trim(); menuToggle.dataset.close = (window.theme && theme.strings.close) || 'Close'; }

  /* ───────── Cart ───────── */
  const cart = {
    drawer: () => $('cart-drawer'),
    open() {
      if (!this.drawer()) return;
      body.classList.add('cart-open');
      $('.drawer', this.drawer()).focus();
    },
    close() { body.classList.remove('cart-open'); },
    // Swap in freshly rendered drawer HTML (from the Section Rendering API) and update counts.
    render(html) {
      const d = this.drawer();
      if (!d || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('.drawer', doc);
      if (fresh) $('.drawer', d).innerHTML = fresh.innerHTML;
      const count = $('[data-drawer-count]', doc);
      if (count) $$('[data-cart-count]').forEach(el => el.textContent = count.textContent);
    },
    async add(formData) {
      const drawer = this.drawer();
      if (drawer) formData.append('sections', 'cart-drawer');
      const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      if (!drawer || body.dataset.cartType !== 'drawer') { location.href = theme.routes.cart; return; }
      this.render(data.sections && data.sections['cart-drawer']);
      this.open();
    },
    async change(line, quantity) {
      const d = this.drawer();
      $('.drawer', d).classList.add('busy');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: location.pathname })
        });
        const data = await res.json();
        this.render(data.sections && data.sections['cart-drawer']);
      } finally {
        $('.drawer', d).classList.remove('busy');
      }
    }
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', e => {
        if (e.target.closest('[data-cart-close]')) { cart.close(); return; }
        const q = e.target.closest('[data-qty]');
        if (q) cart.change(+q.closest('[data-line]').dataset.line, +q.dataset.qty);
      });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', e => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && cart.drawer() && body.dataset.cartType === 'drawer') { e.preventDefault(); cart.open(); }
  });
  addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    cart.close();
    const h = header();
    if (h) { h.setMenu(false); h.setSearch(false); h.openMega(null); }
  });

  // Every add-to-cart form (cards, cart suggestions, product page) goes through here.
  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const err = form.querySelector('[data-form-error]');
    const label = btn.textContent;
    btn.disabled = true;
    btn.classList.add('is-busy');
    if (err) err.hidden = true;
    try {
      await cart.add(new FormData(form));
      btn.textContent = theme.strings.added;
      setTimeout(() => { btn.textContent = label; }, 1600);
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; } else alert(ex.message);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-busy');
    }
  });

  /* ───────── Product page ───────── */
  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.gallery();
      this.stepper();
      this.delivery();
      this.sticky();
      this.sheetNav();
      const json = $('[data-variants]', this);
      if (json) {
        this.variants = JSON.parse(json.textContent);
        this.addEventListener('change', e => { if (e.target.matches('[data-option]')) this.update(); });
      }
      // Hovering a colour previews its name
      const name = $('[data-colour-name]', this);
      if (name) {
        const original = name.textContent;
        $$('[data-swatch-name]', this).forEach(s => {
          s.addEventListener('mouseenter', () => { name.textContent = s.dataset.swatchName; });
          s.addEventListener('mouseleave', () => { name.textContent = original; });
        });
      }
    }

    gallery() {
      const track = $('[data-gallery-track]', this);
      if (!track) return;
      const slides = $$('.gallery__slide', track);
      const thumbs = $$('[data-thumb]', this);
      const current = $('[data-gallery-current]', this);
      const go = i => {
        i = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      };
      this.goTo = go;
      const index = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      let q = false;
      track.addEventListener('scroll', () => {
        if (q) return; q = true;
        requestAnimationFrame(() => {
          q = false;
          const i = index();
          if (current) current.textContent = i + 1;
          thumbs.forEach((t, n) => t.classList.toggle('is-on', n === i));
        });
      }, { passive: true });
      thumbs.forEach(t => t.addEventListener('click', () => go(+t.dataset.thumb)));
      const prev = $('[data-gallery-prev]', this), next = $('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => go(index() - 1));
      if (next) next.addEventListener('click', () => go(index() + 1));
      track.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(index() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(index() - 1); }
      });

      // Zoom
      const dialog = $('[data-zoom-dialog]', this);
      if (!dialog || !dialog.showModal) return;
      const ztrack = $('[data-zoom-track]', dialog);
      $$('[data-zoom]', this).forEach(b => b.addEventListener('click', () => {
        dialog.showModal();
        body.classList.add('zoom-open');
        const fig = ztrack.children[+b.dataset.zoom];
        if (fig) ztrack.scrollTo({ top: fig.offsetTop, behavior: 'instant' });
      }));
      const close = () => { dialog.close(); };
      dialog.addEventListener('close', () => body.classList.remove('zoom-open'));
      $('[data-zoom-close]', dialog).addEventListener('click', close);
      dialog.addEventListener('click', e => { if (e.target === dialog) close(); });
    }

    stepper() {
      $$('[data-step]', this).forEach(b => b.addEventListener('click', () => {
        const input = $('input[name="quantity"]', b.parentElement);
        input.value = Math.max(1, (+input.value || 1) + +b.dataset.step);
      }));
    }

    // "Arrives Mon 12 Oct – Sat 17 Oct." Calendar days from today.
    delivery() {
      const el = $('[data-delivery]', this);
      if (!el) return;
      const lang = document.documentElement.lang || 'en';
      const day = n => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' }); };
      $('[data-delivery-text]', el).textContent = el.dataset.text.replace('[from]', day(+el.dataset.min)).replace('[to]', day(+el.dataset.max));
    }

    sticky() {
      const bar = $('[data-sticky-buy]', this);
      const anchor = $('[data-buy-anchor]', this);
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-on', show);
        bar.setAttribute('aria-hidden', !show);
        $('[data-sticky-add]', bar).tabIndex = show ? 0 : -1;
      }).observe(anchor);
      $('[data-sticky-add]', bar).addEventListener('click', () => {
        const form = $('[data-product-form]', anchor);
        if (form) form.requestSubmit ? form.requestSubmit() : $('[data-add]', form).click();
      });
    }

    // Spec sheet tabs: one part at a time (without JS every part stays visible).
    sheetNav() {
      const sheet = $('[data-tabs]', this);
      if (!sheet) return;
      const links = $$('.sheet__nav a', sheet);
      const parts = links.map(a => $(a.getAttribute('href'), sheet));
      const show = i => {
        links.forEach((a, n) => { a.classList.toggle('is-on', n === i); a.setAttribute('aria-selected', n === i); });
        parts.forEach((p, n) => p && p.classList.toggle('is-on', n === i));
      };
      links.forEach((a, i) => {
        a.setAttribute('role', 'tab');
        a.addEventListener('click', e => { e.preventDefault(); show(i); });
      });
      sheet.classList.add('is-tabbed');
      show(0);
    }

    update() {
      const values = $$('[data-option]:checked', this).map(s => s.value);
      const v = this.variants.find(x => x.options.every((o, i) => o === values[i]));
      const btn = $('[data-add]', this);
      if (!v) { btn.disabled = true; return; }
      $('[data-variant-id]', this).value = v.id;
      btn.disabled = !v.available;
      btn.textContent = v.available ? theme.strings.addToCart : theme.strings.soldOut;
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState({}, '', url);
      if (v.featured_media && this.goTo) {
        const slide = $(`[data-media-id="${v.featured_media.id}"]`, this);
        if (slide) this.goTo(+slide.dataset.index);
      }
      // Re-render price and stock from the server so they use the shop's money format.
      fetch(`${location.pathname}?variant=${v.id}&section_id=${this.dataset.section}`)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-price]', '[data-stock]'].forEach(sel => {
            const fresh = $(sel, doc), cur = $(sel, this);
            if (fresh && cur) cur.innerHTML = fresh.innerHTML;
          });
        });
    }
  }
  customElements.define('product-page', ProductPage);

  /* ───────── Recently viewed ───────── */
  const KEY = 'montague-viewed';
  const readViewed = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
  const pdp = $('product-page');
  if (pdp && pdp.dataset.productHandle) {
    const list = [pdp.dataset.productHandle, ...readViewed().filter(h => h !== pdp.dataset.productHandle)].slice(0, 9);
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  }
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const current = pdp && pdp.dataset.productHandle;
      const handles = readViewed().filter(h => h !== current).slice(0, 4);
      if (!handles.length) return;
      const grid = $('[data-recent-grid]', this);
      const cards = await Promise.all(handles.map(h =>
        fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${h}?view=card`).then(r => r.ok ? r.text() : '').catch(() => '')
      ));
      const html = cards.filter(c => c.includes('class="card"')).join('');
      if (!html) return;
      grid.innerHTML = html;
      this.hidden = false;
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ───────── Product recommendations ───────── */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!('IntersectionObserver' in window)) return this.load();
      const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); this.load(); } }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
    async load() {
      const res = await fetch(this.dataset.url);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = $('product-recommendations', doc);
      if (fresh && fresh.querySelector('.card')) { this.innerHTML = fresh.innerHTML; this.hidden = false; }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ───────── Collection filters: submit on change without a full reload ───────── */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      const toggle = $('[data-filter-toggle]', this);
      const panel = $('[data-filter-panel]', this);
      if (toggle && panel) toggle.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open);
      });
      if (!this.form) return;
      // Listen on the whole bar: the sort select sits outside the form (form= attribute).
      this.addEventListener('change', () => this.apply());
      this.form.addEventListener('submit', e => { e.preventDefault(); this.apply(); });
      this.addEventListener('click', e => {
        const a = e.target.closest('[data-filter-link]');
        if (!a) return;
        e.preventDefault();
        this.load(a.href);
      });
    }
    apply() {
      const params = new URLSearchParams(new FormData(this.form));
      // Drop empty price inputs so they do not filter
      for (const [k, v] of [...params]) if (v === '') params.delete(k);
      this.load(`${location.pathname}?${params}`);
    }
    async load(url) {
      const grid = $('[data-collection-results]');
      if (grid) grid.classList.add('busy');
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.dataset.section);
      try {
        const html = await (await fetch(u)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-collection-results]', '[data-filter-count]', '[data-active-filters]', '[data-filter-body]', '[data-result-count]'].forEach(sel => {
          const fresh = $(sel, doc), cur = $(sel);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        u.searchParams.delete('section_id');
        history.replaceState({}, '', u);
      } finally {
        if (grid) grid.classList.remove('busy');
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  /* ───────── Banner video: with separate desktop and mobile files, load only the visible one ───────── */
  $$('[data-banner-video]').forEach(v => {
    if (getComputedStyle(v).display === 'none') { v.remove(); return; }
    v.muted = true;
    v.preload = 'auto';
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  });

  /* ───────── Card gallery: swipe or use the arrows to see a product's other photos ───────── */
  class CardGallery extends HTMLElement {
    connectedCallback() {
      const track = $('[data-card-track]', this);
      const slides = $$('.card__slide', track);
      const dots = $('[data-card-dots]', this);
      const prev = $('[data-card-prev]', this), next = $('[data-card-next]', this);
      if (slides.length < 2) { prev.remove(); next.remove(); return; }
      dots.innerHTML = slides.map((s, i) => `<i${i ? '' : ' class="is-on"'}></i>`).join('');
      const index = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const go = i => track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, i)) * track.clientWidth, behavior: 'smooth' });
      const sync = () => {
        const i = index();
        [...dots.children].forEach((d, n) => d.classList.toggle('is-on', n === i));
        prev.disabled = i === 0;
        next.disabled = i === slides.length - 1;
      };
      let q = false;
      track.addEventListener('scroll', () => { if (!q) { q = true; requestAnimationFrame(() => { q = false; sync(); }); } }, { passive: true });
      prev.addEventListener('click', () => go(index() - 1));
      next.addEventListener('click', () => go(index() + 1));
    }
  }
  customElements.define('card-gallery', CardGallery);

  /* ───────── Reveal on scroll ───────── */
  if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    $$('.rv').forEach(el => io.observe(el));
    document.documentElement.classList.add('js-rv');
  }
})();
